import React, { useState, useEffect, useRef } from 'react';
import { FaSearch } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import Loader from '../../Components/Services/Loader';

const IngredientDatabase = ({ role }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [ingredients, setIngredients] = useState([]);
    const [filteredIngredients, setFilteredIngredients] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(false); // Loading state
    const [error, setError] = useState(null); // Error state
    const navigate = useNavigate();
    const topScrollbarRef = useRef(null);
    const tableScrollbarRef = useRef(null);

    const itemsPerPage = 25;
    // const totalPages = Math.floor(filteredIngredients.length / itemsPerPage);

    // Function to fetch ingredients from the API
    const fetchIngredients = async () => {
        try {
            setLoading(true); // Start loading
            const response = await fetch(`${process.env.REACT_APP_BASE_URI}/elixir/fetch_ingredient`, {
                method: 'GET',
                headers: {
                    Accept: 'application/json',
                },
            });

            



            const { data } = await response.json();
        
            // const timestamp = "2025-01-07T06:39:51";
            // const time = new Date(timestamp).toLocaleTimeString("en-US", { hour12: false });

            if (!response.ok) {
                throw new Error(`HTTP Error ${response.status}: ${response.statusText}`);
            }


            if (Array.isArray(data)) {
                

                const sorted = [...data].sort((a, b) =>
                    stripHtml(a.ing_name).toLowerCase().localeCompare(stripHtml(b.ing_name).toLowerCase())
                );


                setIngredients(sorted);
                setFilteredIngredients(sorted);
                setError(null); // Clear any previous error
            } else {
                setError('Data format error.');
            }


        } catch (error) {
            setError('Failed to fetch ingredients. Please refresh page!');
            if (error.status.includes("400")) {
                alert("Bad request! Please check your input.");
            } else if (error.status.includes("401")) {
                alert("Unauthorized! Please log in again.");
            } else if (error.status.includes("403")) {
                alert("Access denied! You don't have permission.");
            } else if (error.status.includes("404")) {
                alert("Resource not found!");
            } else if (error.status.includes("422")) {
                alert("Validation error! Please correct your input.");
            } else if (error.status.includes("500")) {
                alert("Server error! Try again later.");
            } else {
                alert("An unknown error occurred.");
            }
        } finally {
            setLoading(false); // Stop loading
        }


    };

    // Fetch ingredients when the component mounts
    useEffect(() => {
        fetchIngredients();

    }, []);

    useEffect(() => {
        setTotalPages(Math.ceil(filteredIngredients.length / itemsPerPage))
    }, [filteredIngredients])

    const syncScroll = (source) => {
        if (source === "top" && tableScrollbarRef.current) {
            tableScrollbarRef.current.scrollLeft = topScrollbarRef.current.scrollLeft;
        }
        if (source === "table" && topScrollbarRef.current) {
            topScrollbarRef.current.scrollLeft = tableScrollbarRef.current.scrollLeft;
        }
    };

    const stripHtml = (html) => {
        const div = document.createElement("div");
        div.innerHTML = html;
        return div.textContent || div.innerText || "";
    };

    // Search functionality
    const handleSearch = (e) => {

        setPage(1);
        const term = e.target.value.toLowerCase();
        setSearchTerm(term);

        if (!term) {
            setFilteredIngredients(ingredients);
            return
        }

   

        const filtered = ingredients.filter((ingredient) => {
            const name = stripHtml(ingredient.ing_name).toLowerCase();
            const code = ingredient.ing_item_code.toLowerCase();
            const vendor = ingredient.vendor.toLowerCase();
            const rmCode = ingredient.rm_code.toLowerCase();

            return name.includes(term.toLowerCase()) ||
                code.includes(term.toLowerCase()) ||
                vendor.includes(term.toLowerCase()) ||
                rmCode.includes(term.toLowerCase());
        }
        );



        const sortedFiltered = [...filtered].sort((a, b) =>
            stripHtml(a.ing_name).toLowerCase().localeCompare(stripHtml(b.ing_name).toLowerCase())
        );

        setFilteredIngredients(sortedFiltered);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A'; // Return 'N/A' if the date is null or undefined
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'N/A'; // Return 'N/A' if the date is invalid
        return date.toLocaleDateString('en-GB'); // Formats to DD/MM/YYYY
    };


    // Navigate to IngredientItem component when clicking a row
    const handleRowClick = (itemCode) => {
        navigate(`/ingredient/${itemCode}`);
    };

    const handlePageChange = (pageNum) => {
        if (pageNum > 0 && pageNum <= Math.ceil(filteredIngredients.length / itemsPerPage) && page !== pageNum) {
            setPage(pageNum)
        }
    }

    const getPaginationRange = (currentPage, totalPages, siblingCount = 1) => {
        // const totalPageNumbersToShow = siblingCount * 2 + 5; // 5 = current, first, last, 2 dots
        const totalPageNumbersToShow = 4;
        const showPagesAtStart = 3;
        const showPagesAtEnd = 3;
        if (totalPageNumbersToShow >= totalPages) {
            return Array.from({ length: totalPages }, (_, i) => i + 1);
        }

        if (totalPages <= 5) {
            // Show all pages if total pages are small
            return Array.from({ length: totalPages }, (_, i) => i + 1);
        }
        if (currentPage <= showPagesAtStart) {
            // Near start
            return [1, 2, 3, 4, '...', totalPages];
        }

        if (currentPage >= totalPages - 2) {
            // Near end
            return [1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
        }

        // Middle range
        return [1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages];

        // const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
        // const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPages);

        // const shouldShowLeftDots = leftSiblingIndex > 2;
        // const shouldShowRightDots = rightSiblingIndex < totalPages - 1;

        // if (!shouldShowLeftDots && shouldShowRightDots) {
        //     const leftRange = Array.from({ length: 3 + 2 * siblingCount }, (_, i) => i + 1);
        //     return [...leftRange, '...', totalPages];
        // }

        // if (shouldShowLeftDots && !shouldShowRightDots) {
        //     const rightRangeStart = totalPages - (2 * siblingCount + 2);
        //     const rightRange = Array.from({ length: 3 + 2 * siblingCount }, (_, i) => rightRangeStart + i);
        //     return [1, '...', ...rightRange];
        // }

        // if (shouldShowLeftDots && shouldShowRightDots) {
        //     const middleRange = Array.from({ length: rightSiblingIndex - leftSiblingIndex + 1 }, (_, i) => leftSiblingIndex + i);
        //     return [1, '...', ...middleRange, '...', totalPages];
        // }

        // // Final fallback return (should never be hit, but ensures no undefined)
        // return Array.from({ length: totalPages }, (_, i) => i + 1);
    };


    if (loading) {
        return <Loader />
    }



    return (
        <div className='min-h-screen bg-slate-700 p-4'>
            <div className='flex flex-col sm:flex-row justify-between items-center gap-2'>
                <button
                    onClick={() => { navigate("/database") }}
                    type='button'
                    className='bg-gray-900 hover:bg-gray-600 text-white py-2 px-4 rounded shadow sm:mb-5 sm:mt-5 w-full sm:w-auto'>
                    Back
                </button>
            </div>

            <div className='flex flex-col sm:flex-row justify-between items-center gap-4 mt-4 mb-5'>
                <div className='w-full sm:w-2/3 md:w-1/2 lg:w-1/2'>
                    <div className='relative w-full'>
                        <FaSearch className='absolute left-3 top-3 text-white' />
                        <input
                            type='text'
                            placeholder='Search ingredients...'
                            value={searchTerm}
                            onChange={handleSearch}
                            className='pl-10 bg-gray-900 text-white rounded w-full px-4 py-2'
                        />
                    </div>
                </div>

                <button
                    className='w-full sm:w-auto bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded'
                    onClick={() => navigate('/ingredientdb')}
                >
                    + Add Ingredient
                </button>
            </div>

            {error ? (
                <div className='text-center text-red-500'>{error}</div>
            ) : (
                <div className="relative w-full">
                    {/* Scrollbar on top */}
                    <div
                        ref={topScrollbarRef}
                        className="w-full overflow-x-auto h-4 scrollbar-thin scrollbar-thumb-gray-500 scrollbar-track-gray-900"
                        onScroll={() => syncScroll("top")}
                    >
                        <div className="w-[1800px] h-1"></div> {/* Fake scrollbar width matching the table */}
                    </div>
                    {/* Pagination Controls */}
                    <div className="flex justify-center items-center mt-6 px-2">
                        <div className="flex flex-wrap sm:flex-nowrap items-center gap-2 max-w-full overflow-x-auto">
                            {/* Prev Button */}
                            <button
                                onClick={() => handlePageChange(page - 1)}
                                disabled={page === 1}
                                className="px-3 py-1 sm:px-4 sm:py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-blue-300 transition"
                            >
                                Prev
                            </button>

                            {/* Page Buttons */}
                            {getPaginationRange(
                                page,
                                Math.ceil(filteredIngredients.length / itemsPerPage),
                            ).map((pg, idx) => (
                                <button
                                    key={idx}
                                    onClick={() => typeof pg === "number" && handlePageChange(pg)}
                                    disabled={pg === "..."}
                                    className={`min-w-[36px] h-9 rounded-md text-sm font-medium transition
                                            ${pg === "..."
                                            ? "cursor-default text-gray-500 px-2"
                                            : page === pg
                                                ? "bg-blue-700 text-white"
                                                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                                        }`}
                                >
                                    {pg}
                                </button>
                            ))}

                            {/* Next Button */}
                            <button
                                onClick={() => handlePageChange(page + 1)}
                                disabled={page === Math.ceil(filteredIngredients.length / itemsPerPage)}
                                className="px-3 py-1 sm:px-4 sm:py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-blue-300 transition"
                            >
                                Next
                            </button>
                        </div>
                    </div>



                    {/* Scrollable Table */}
                    <div
                        ref={tableScrollbarRef}
                        className="py-4 overflow-x-auto rounded-2xl"
                        onScroll={() => syncScroll("table")}
                    >
                        <table className="text-white w-full border-separate border-spacing-0 rounded-2xl min-w-[1600px]">
                            <thead className="bg-gray-900">
                                <tr>
                                    <th className="px-4 sm:px-4 py-2">Ingredient Name</th>
                                    <th className="px-4 sm:px-4 py-2">Item Code</th>
                                    <th className="px-4 sm:px-4 py-2">Vendor Name</th>
                                    <th className="px-4 sm:px-4 py-2">RM Code</th>
                                    <th className="px-4 sm:px-4 py-2">Cas Number</th>
                                    <th className="px-4 sm:px-4 py-2">Ec Number</th>
                                    <th className="px-4 sm:px-4 py-2">Source</th>
                                    <th className="px-4 sm:px-4 py-2">Source Type</th>
                                    <th className="px-4 sm:px-4 py-2">Country Of Origin</th>
                                    <th className="px-4 sm:px-4 py-2">Calories</th>
                                    <th className="px-4 sm:px-4 py-2">Fats</th>
                                    <th className="px-4 sm:px-4 py-2">Carbohydrates</th>
                                    <th className="px-4 sm:px-4 py-2">Moisture</th>
                                    <th className="px-4 sm:px-4 py-2">Ash</th>
                                    <th className="px-4 sm:px-4 py-2">Created At</th>
                                    <th className="px-4 sm:px-4 py-2">Updated At</th>
                                </tr>
                            </thead>
                            <tbody>
                                {

                                    filteredIngredients.slice((page - 1) * itemsPerPage, (page) * itemsPerPage).map((ingredient, index) => (
                                        <tr
                                            key={index}
                                            className="bg-gray-800 hover:bg-gray-700 text-center cursor-pointer text-xs sm:text-sm"
                                            onClick={() => handleRowClick(ingredient.ing_item_code)}
                                        >
                                            <td className="px-2 sm:px-4 py-2 whitespace-nowrap" dangerouslySetInnerHTML={{ __html: ingredient.ing_name }}></td>
                                            <td className="px-2 sm:px-4 py-2 whitespace-nowrap">{ingredient.ing_item_code}</td>
                                            <td className="px-2 sm:px-4 py-2 whitespace-nowrap">{ingredient.vendor}</td>
                                            <td className="px-2 sm:px-4 py-2 whitespace-nowrap">{ingredient.rm_code}</td>
                                            <td className="px-4 sm:px-4 py-2" style={{ whiteSpace: 'pre-line' }}>{ingredient.cas_num}</td>
                                            <td className="px-4 sm:px-4 py-2" style={{ whiteSpace: 'pre-line' }}>{ingredient.ec_num}</td>
                                            <td className="px-4 sm:px-4 py-2" dangerouslySetInnerHTML={{ __html: ingredient.source }}></td>
                                            <td className="px-4 sm:px-4 py-2">{ingredient.source_type}</td>
                                            <td className="px-4 sm:px-4 py-2">{ingredient.country_origin}</td>
                                            <td className="px-4 sm:px-4 py-2">{ingredient.calories}</td>
                                            <td className="px-4 sm:px-4 py-2">{ingredient.fat}</td>
                                            <td className="px-4 sm:px-4 py-2">{ingredient.carbohydrates}</td>
                                            <td className="px-4 sm:px-4 py-2">{ingredient.moisture}</td>
                                            <td className="px-4 sm:px-4 py-2">{ingredient.ash}</td>
                                            <td className="px-4 sm:px-4 py-2">{formatDate(ingredient.created_at)}</td>
                                            <td className="px-4 sm:px-4 py-2">{formatDate(ingredient.updated_at)}</td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Controls */}
                    <div className="flex justify-center items-center mt-6 px-2">
                        <div className="flex flex-wrap sm:flex-nowrap items-center gap-2 max-w-full overflow-x-auto">
                            {/* Prev Button */}
                            <button
                                onClick={() => handlePageChange(page - 1)}
                                disabled={page === 1}
                                className="px-3 py-1 sm:px-4 sm:py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-blue-300 transition"
                            >
                                Prev
                            </button>

                            {/* Page Buttons */}
                            {getPaginationRange(
                                page,
                                Math.ceil(filteredIngredients.length / itemsPerPage),
                            ).map((pg, idx) => (
                                <button
                                    key={idx}
                                    onClick={() => typeof pg === "number" && handlePageChange(pg)}
                                    disabled={pg === "..."}
                                    className={`min-w-[36px] h-9 rounded-md text-sm font-medium transition
                                            ${pg === "..."
                                            ? "cursor-default text-gray-500 px-2"
                                            : page === pg
                                                ? "bg-blue-700 text-white"
                                                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                                        }`}
                                >
                                    {pg}
                                </button>
                            ))}

                            {/* Next Button */}
                            <button
                                onClick={() => handlePageChange(page + 1)}
                                disabled={page === Math.ceil(filteredIngredients.length / itemsPerPage)}
                                className="px-3 py-1 sm:px-4 sm:py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-blue-300 transition"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>

    );
};

export default IngredientDatabase;
